import { Button } from "@volca/ui/components/ui/button";
import { SheetApprovalControl } from "@/payroll/approvals/sheet-approval-control";
import { getCellFormatType } from "./adapter";
import type { AiScriptSpreadsheetViewerProps } from "./ai-script-spreadsheet-viewer-types";
import { ColumnValueTypeControl } from "./column-value-type-control";
import { FormulaBar } from "./formula-bar";
import { isOptimisticManualCell } from "./optimistic-manual-row-edits";
import { SheetSearchControl } from "./sheet-search-control";
import type { SpreadsheetViewer } from "./use-spreadsheet-viewer";

type ViewerFormulaBarProps = Pick<
  AiScriptSpreadsheetViewerProps,
  "onViewEditedCell" | "onSheetApprovalChange" | "pendingSheetApproval"
> & {
  viewer: SpreadsheetViewer;
};

export const ViewerFormulaBar = ({
  viewer,
  onViewEditedCell,
  onSheetApprovalChange,
  pendingSheetApproval,
}: ViewerFormulaBarProps) => {
  const {
    selected: {
      selectedCoordinate,
      formulaBarCommitTarget,
      formulaBarValue,
      selectedCellErrorCode,
      isNewRowSelected,
      selectedCellRecord,
      selectedEditedCell,
      selectedColumnIndex,
      selectedCol,
      selectedRow,
    },
    sheet: {
      canMutateActiveSheet,
      isActiveSheetEditable,
      hasActiveRegionView,
      activeSheet,
      activeSheetApproval,
      activeLoadedSheet,
    },
    mutations: {
      isCreatingManualRow,
      isUpdatingAnyCell,
      isBulkPasting,
      isUpdatingColumnValueType,
    },
    workbookOperation,
    retryUnsavedChanges,
    isRetryingUnsavedChanges,
    history,
    isHistoryBlocked,
    undo,
    redo,
    ui: { isMaximized, setIsMaximized },
    cellEdits: { formatCell, handleFormulaBarCommit },
    columnValueType: { canChangeColumnValueType, changeColumnValueType },
    regions: { clearActiveRegionViews },
    search: { controlProps: sheetSearchControlProps },
    pointer: { getCellLink, getCellSource },
  } = viewer;

  return (
    <FormulaBar
      coordinate={selectedCoordinate}
      commitTarget={formulaBarCommitTarget}
      value={formulaBarValue}
      errorCode={selectedCellErrorCode}
      canEdit={!!formulaBarCommitTarget}
      addRowMode={canMutateActiveSheet && isNewRowSelected}
      isSheetReadOnly={!isActiveSheetEditable}
      isCellLocked={selectedCellRecord?.locked === true}
      isRecalculating={
        workbookOperation.isPending ||
        isCreatingManualRow ||
        isUpdatingAnyCell ||
        isBulkPasting ||
        isUpdatingColumnValueType
      }
      operationError={workbookOperation.errorMessage}
      onRetryOperation={workbookOperation.retry ?? undefined}
      hasUnsavedChanges={activeLoadedSheet?.cells.some(
        (cell) => cell.manual_row_save_failed,
      )}
      onRetryUnsavedChanges={retryUnsavedChanges}
      isRetryingUnsavedChanges={isRetryingUnsavedChanges}
      canUndo={history.canUndo && !isHistoryBlocked}
      canRedo={history.canRedo && !isHistoryBlocked}
      undoLabel={history.undoLabel}
      redoLabel={history.redoLabel}
      isUndoRedoApplying={history.isApplying}
      onUndo={undo}
      onRedo={redo}
      valueType={
        selectedCellRecord ? getCellFormatType(selectedCellRecord) : null
      }
      onFormatChange={
        canMutateActiveSheet &&
        selectedCellRecord &&
        !selectedCellRecord.locked &&
        !isOptimisticManualCell(selectedCellRecord)
          ? (vt) => formatCell(selectedCellRecord, vt)
          : undefined
      }
      cellLink={
        selectedCol !== null && selectedRow !== null
          ? getCellLink(selectedCol, selectedRow)
          : null
      }
      onShowSource={
        selectedCol !== null && selectedRow !== null
          ? getCellSource(selectedCol, selectedRow)
          : null
      }
      onCommit={handleFormulaBarCommit}
      isMaximized={isMaximized}
      onToggleMaximize={() => setIsMaximized((prev) => !prev)}
      onViewEdit={
        selectedEditedCell && onViewEditedCell
          ? () => onViewEditedCell(selectedEditedCell)
          : undefined
      }
      rightAccessory={
        <>
          {selectedColumnIndex !== undefined && (
            <ColumnValueTypeControl
              columnIndex={selectedColumnIndex}
              disabled={!canChangeColumnValueType}
              onValueTypeChange={(col, valueType) =>
                void changeColumnValueType(col, valueType)
              }
            />
          )}
          {hasActiveRegionView && (
            <Button variant="ghost" size="xs" onClick={clearActiveRegionViews}>
              Clear filters and sorts
            </Button>
          )}
          {activeSheetApproval && (
            <SheetApprovalControl
              accessKey={activeSheetApproval}
              sheetId={activeSheet?.id}
              onApprovalChange={onSheetApprovalChange}
              pendingApproval={pendingSheetApproval}
            />
          )}
          <SheetSearchControl {...sheetSearchControlProps} />
        </>
      }
    />
  );
};
